import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '../../i18n/format'
import { AppUseSelector } from '../../redux/hooks'
import { getStripePlan, stripeCheckoutLink } from '../../redux/actions/myAccountAction'
import { getPriceInNumber } from '../../utils/helperFunction'
import { membershipNameType, PlanChangeLink } from '../../utils/constant'
import { captureWeeklyEvent } from '../../utils/posthogWeekly'
import { isWeeklyOfferAudience, isWeeklyHardGateActive } from '../../utils/weeklyOffer'
import { DRAGON_BOAT_PROMO, getDragonBoatPromoPricing, isDragonBoatPromoActive } from '../../utils/dragonBoatPromo'
import './weekly-offer-gate.css'

interface WeeklyOfferGateProps {
  // Where the gate is mounted — used only for analytics segmentation.
  placement: 'stories' | 'words'
}

type PlanValue = string
type Billing = 'monthly' | 'yearly'

// Value-first copy for each plan. Prices always come from Stripe; only the framing
// lives here. Weekly is the entry tier, Daily/Pro the step-ups — all shown evenly.
// displayName is the plan's name and stays as is; the rest are `offers` keys.
const PLAN_COPY: Record<PlanValue, { displayName: string; cadence: string; points: string[]; cta: string }> = {
  [membershipNameType.Weekly]: {
    displayName: 'Weekly',
    cadence: 'offers:weeklyGate.plans.weekly.cadence',
    points: [
      'offers:weeklyGate.plans.weekly.points.lesson',
      'offers:weeklyGate.plans.weekly.points.audio',
      'offers:weeklyGate.plans.weekly.points.definitions',
      'offers:weeklyGate.plans.weekly.points.history',
    ],
    cta: 'offers:weeklyGate.plans.weekly.cta',
  },
  [membershipNameType.Standard]: {
    displayName: 'Daily',
    cadence: 'offers:weeklyGate.plans.daily.cadence',
    points: ['offers:weeklyGate.plans.daily.points.everyDay', 'offers:weeklyGate.plans.daily.points.prompts'],
    cta: 'offers:weeklyGate.plans.daily.cta',
  },
  [membershipNameType.Pro]: {
    displayName: 'Pro',
    cadence: 'offers:weeklyGate.plans.pro.cadence',
    points: [
      'offers:weeklyGate.plans.pro.points.everythingDaily',
      'offers:weeklyGate.plans.pro.points.teacher',
      'offers:weeklyGate.plans.pro.points.feedback',
    ],
    cta: 'offers:weeklyGate.plans.pro.cta',
  },
}

// Weekly first as the entry tier, then the step-ups. No tier is singled out.
const PLAN_ORDER = [membershipNameType.Weekly, membershipNameType.Standard, membershipNameType.Pro]

// A single Stripe price (one billing period) for one tier.
interface PriceView {
  priceId: string
  amount: number // the charge for that period: monthly price, or full yearly price
}

// Per tier we keep both billing periods; either may be null (Weekly is annual-only).
interface TierPlans {
  value: PlanValue
  monthly: PriceView | null
  yearly: PriceView | null
}

// Pro monthly + its 50%-off-for-3-months pricing, derived live from Stripe during the Dragon
// Boat window. Null when the window is closed or the monthly Pro price is unavailable.
interface ProPromoView {
  priceId: string
  regularMonthly: number
  promoMonthly: number
}

// All numbers here are derived from the Stripe price returned by the API — never hard-coded.
const buildPrice = (raw: any): PriceView | null => {
  if (!raw?.value) return null
  const amount = getPriceInNumber(raw.price)
  if (!amount || isNaN(amount)) return null
  return { priceId: raw.priceId, amount }
}

// Prices here come from the legacy `$` price string, so they are USD.
const usd = (amount: number, digits: number) =>
  formatNumber(amount, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const fmtMonthly = (m: number) => usd(m, m < 10 ? 2 : 0)

const Tick = () => (
  <svg className='wog-tick' width='15' height='15' viewBox='0 0 16 16' aria-hidden='true'>
    <path
      d='M3.5 8.6l2.9 2.9L12.7 4.7'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const WeeklyOfferGate: React.FC<WeeklyOfferGateProps> = ({ placement }) => {
  const { t } = useTranslation('offers')
  const navigate = useNavigate()
  const { membership, credentials } = AppUseSelector(({ user }) => user)

  const audience = isWeeklyOfferAudience(membership, credentials)
  const hardGate = isWeeklyHardGateActive()
  const dragonBoatActive = isDragonBoatPromoActive()

  // Annual plans show by default, except while the Dragon Boat promo is live — then we open on
  // Monthly so the 50%-off Pro offer is visible without toggling. Learners can switch either way.
  // Weekly is annual-only, so it always renders its yearly price.
  const [billing, setBilling] = useState<Billing>(dragonBoatActive ? 'monthly' : 'yearly')
  const [plans, setPlans] = useState<TierPlans[] | null>(null)
  const [proPromo, setProPromo] = useState<ProPromoView | null>(null)
  const [checkoutBusy, setCheckoutBusy] = useState<string | null>(null)

  // The gate is a hard, non-dismissible paywall for its audience (free weekly-test-cohort
  // learners). There is intentionally no close affordance — paid learners (including paid cohort
  // members) are never in-audience, so this never blocks anyone who is already on a plan.
  const open = audience

  // Fetch the cohort-scoped plans only when the gate is actually shown. Every displayed price
  // comes from this Stripe response — including the Dragon Boat monthly Pro promo.
  useEffect(() => {
    if (!open || plans !== null) return
    let cancelled = false
    getStripePlan(credentials?.memberId).then((res: any) => {
      if (cancelled) return
      const monthly: any[] = res?.monthly || []
      const yearly: any[] = res?.yearly || []
      const tiers = PLAN_ORDER.map((v) => ({
        value: v,
        monthly: buildPrice(monthly.find((p) => p.value === v)),
        yearly: buildPrice(yearly.find((p) => p.value === v)),
      })).filter((tier) => tier.monthly || tier.yearly)
      setPlans(tiers)

      if (dragonBoatActive) {
        const proMonthly = monthly.find((p) => p.value === membershipNameType.Pro)
        const pricing = getDragonBoatPromoPricing(getPriceInNumber(proMonthly?.price))
        if (proMonthly?.priceId && pricing) {
          setProPromo({
            priceId: proMonthly.priceId,
            regularMonthly: pricing.regularMonthly,
            promoMonthly: pricing.promoMonthly,
          })
        }
      }
    })
    return () => {
      cancelled = true
    }
  }, [open, plans, credentials?.memberId, dragonBoatActive])

  // Diagnostics: cohort member saw the homepage paywall.
  useEffect(() => {
    if (open) captureWeeklyEvent('weekly_offer_plan_viewed', { surface: `gate_${placement}`, hard_gate: hardGate })
  }, [open, placement, hardGate])

  // Lock body scroll while the gate is open. The gate is non-dismissible, so there is no Esc or
  // overlay-click handler to close it.
  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  const handleCheckout = useCallback(
    async ({ value, priceId, couponCode }: { value: PlanValue; priceId: string; couponCode?: string }) => {
      if (checkoutBusy) return
      if (value === membershipNameType.Weekly)
        captureWeeklyEvent('weekly_checkout_started', { surface: `gate_${placement}` })
      if (!priceId) {
        navigate(PlanChangeLink)
        return
      }
      setCheckoutBusy(value)
      try {
        const affiliateId = (window as any).affiliateId
        const payload: any = { priceId }
        if (affiliateId) payload.affiliateId = String(affiliateId)
        if (couponCode) payload.couponCode = couponCode
        const res: any = await stripeCheckoutLink(payload)
        if (res?.return_url) {
          window.open(res.return_url, '_self')
          return
        }
        if (res?.url) {
          window.open(res.url, '_self')
          return
        }
        navigate(PlanChangeLink)
      } catch {
        navigate(PlanChangeLink)
      } finally {
        setCheckoutBusy(null)
      }
    },
    [checkoutBusy, navigate, placement],
  )

  if (!open) return null

  return (
    <div className='wog-overlay' role='dialog' aria-modal='true' aria-labelledby='wog-title'>
      <div className='wog-modal'>
        <header className='wog-head'>
          <h2 id='wog-title' className='wog-headline'>
            {t('weeklyGate.title')}
          </h2>
          <p className='wog-lede'>{t('weeklyGate.lede')}</p>
        </header>

        <div className='wog-billing'>
          <div className='wog-billing-group' role='tablist' aria-label={t('weeklyGate.billingPeriod')}>
            {(['yearly', 'monthly'] as Billing[]).map((b) => (
              <button
                key={b}
                type='button'
                role='tab'
                aria-selected={billing === b}
                className={`wog-billing-btn${billing === b ? ' active' : ''}`}
                onClick={() => setBilling(b)}
              >
                {b === 'yearly' ? t('weeklyGate.yearly') : t('weeklyGate.monthly')}
              </button>
            ))}
          </div>
          <span className='wog-billing-note'>
            {dragonBoatActive
              ? t('weeklyGate.promoNote', { percent: DRAGON_BOAT_PROMO.discountPercent })
              : t('weeklyGate.saveYearly')}
          </span>
        </div>

        <div className='wog-tiers'>
          {plans === null ? (
            <div className='wog-loading'>{t('weeklyGate.loading')}</div>
          ) : (
            plans.map((tier, i) => {
              const copy = PLAN_COPY[tier.value]
              if (!copy) return null
              const isPro = tier.value === membershipNameType.Pro
              // Weekly is annual-only — it always renders its yearly price regardless of the toggle.
              const showMonthly = billing === 'monthly' && Boolean(tier.monthly)
              const promo = isPro && showMonthly ? proPromo : null
              // In yearly view, surface the Dragon Boat offer with a hint that switches to monthly.
              const showProPromoHint = isPro && !showMonthly && Boolean(proPromo)

              // Resolve the price to display (big number is always a per-month figure).
              let bigPerMonth: number
              let billNote: React.ReactNode
              let checkoutPriceId = ''
              let checkoutCoupon: string | undefined
              let ctaLabel = t(copy.cta)

              if (promo) {
                bigPerMonth = promo.promoMonthly
                billNote = (
                  <>
                    <span className='wog-tier-was'>
                      {t('weeklyGate.promoWas', { price: usd(promo.regularMonthly, 0) })}
                    </span>
                    <span className='wog-tier-billnote'>
                      {t('weeklyGate.promoBillNote', {
                        months: DRAGON_BOAT_PROMO.durationMonths,
                        price: usd(promo.regularMonthly, 0),
                      })}
                    </span>
                    <span className='wog-tier-save'>
                      {t('weeklyGate.promoSave', { percent: DRAGON_BOAT_PROMO.discountPercent })}
                    </span>
                  </>
                )
                checkoutPriceId = promo.priceId
                checkoutCoupon = DRAGON_BOAT_PROMO.couponCode
                ctaLabel = t('weeklyGate.promoCta', { percent: DRAGON_BOAT_PROMO.discountPercent })
              } else if (showMonthly && tier.monthly) {
                bigPerMonth = tier.monthly.amount
                billNote = <span className='wog-tier-billnote'>{t('weeklyGate.billedMonthly')}</span>
                checkoutPriceId = tier.monthly.priceId
              } else if (tier.yearly) {
                // Yearly plan — show the monthly equivalent with the full yearly charge below.
                bigPerMonth = tier.yearly.amount / 12
                billNote = (
                  <>
                    <span className='wog-tier-now'>{usd(tier.yearly.amount, 0)}</span>
                    <span className='wog-tier-billnote'>
                      {billing === 'monthly' && !tier.monthly
                        ? t('weeklyGate.billedYearlyAnnualPlan')
                        : t('weeklyGate.billedYearly')}
                    </span>
                  </>
                )
                checkoutPriceId = tier.yearly.priceId
              } else {
                return null
              }

              return (
                <article
                  key={tier.value}
                  className={`wog-tier${promo ? ' wog-tier--promo' : ''}`}
                  style={{ animationDelay: `${0.08 * i + 0.12}s` }}
                >
                  {promo && (
                    <span className='wog-tier-flag wog-tier-flag--promo'>
                      {t('weeklyGate.promoFlag', { percent: DRAGON_BOAT_PROMO.discountPercent })}
                    </span>
                  )}
                  <div className='wog-tier-head'>
                    <h3 className='wog-tier-name'>{copy.displayName}</h3>
                    <p className='wog-tier-cadence'>{t(copy.cadence)}</p>
                  </div>

                  <div className='wog-tier-price'>
                    <span className='wog-tier-amount'>{fmtMonthly(bigPerMonth)}</span>
                    <span className='wog-tier-per'>{t('weeklyGate.perMonth')}</span>
                  </div>
                  <div className='wog-tier-bill'>{billNote}</div>

                  {showProPromoHint && (
                    <button type='button' className='wog-tier-promo-hint' onClick={() => setBilling('monthly')}>
                      {t('weeklyGate.promoHint', {
                        percent: DRAGON_BOAT_PROMO.discountPercent,
                        months: DRAGON_BOAT_PROMO.durationMonths,
                      })}
                    </button>
                  )}

                  <ul className='wog-tier-points'>
                    {copy.points.map((pt) => (
                      <li key={pt}>
                        <Tick />
                        <span>{t(pt)}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type='button'
                    className={`wog-tier-cta${promo ? ' wog-tier-cta--promo' : ''}`}
                    onClick={() =>
                      handleCheckout({ value: tier.value, priceId: checkoutPriceId, couponCode: checkoutCoupon })
                    }
                    disabled={checkoutBusy === tier.value}
                  >
                    {checkoutBusy === tier.value ? t('weeklyGate.openingCheckout') : ctaLabel}
                  </button>
                </article>
              )
            })
          )}
        </div>

        <footer className='wog-foot'>
          <span className='wog-reassure'>{t('weeklyGate.reassure')}</span>
          <span className='wog-reassure'>{t('common:teacherLanguageNotice')}</span>
        </footer>
      </div>
    </div>
  )
}

export default WeeklyOfferGate
